import React, { Fragment, useContext, useEffect, useMemo, useState } from 'react';

import { Button, Loading as LoadingComponent, Modal, Steps } from '~/components';
import history from '~/config/history';
import { Context as QueryContext } from '~/contexts/QueryContext';
import { PATHS } from '~/routes/paths';
import api, { Endpoints } from '~/services/api';

import { Board, Line, Message, Title, TextModal } from './styles';

function ValorExcedente() {
  const { params } = useContext(QueryContext);
  const [modalVisible, setVisible] = useState(undefined);
  const [steps, setSteps] = useState([]);
  const [values, setValues] = useState(undefined);
  const data = useMemo(() => history?.location?.state, []);

  useEffect(() => {
    if (!data) {
      history.replace(PATHS.loading);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const getStatus = async () => {
      try {
        const { data: status } = await api.get(Endpoints.BUSCAR_STATUS + params.idAssistServico);

        setSteps(
          status.map(
            ({ status: { descricao }, dataTerminoStatus: dataStatus = null, ...rest }) => ({
              ...rest,
              name: descricao,
              isCompleted: !!dataStatus,
              date: dataStatus ? new Date(dataStatus) : undefined,
            })
          )
        );
      } catch (error) {
        console.error(error);
      }
    };

    if (params?.idAssistServico) getStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const getValues = async () => {
      try {
        const { data: dataValues } = await api.get(
          Endpoints.VALORES_EXCEDENTES + params.idAssistServico
        );

        setValues(() => ({
          idValorServico: dataValues.idValorServico,
          plano: dataValues.descricaoPlano,
          cobertura: dataValues.valorLimite,
          total: dataValues.valorTotalServico,
          excedente: dataValues.excedente ? dataValues.valorExcedente : 0,
          procedimentos: dataValues.detalhesValoresList.map(
            ({ idDetalheValorServico, descricao, valorServico, ...rest }) => ({
              ...rest,
              id: String(idDetalheValorServico),
              descricao,
              valor: valorServico,
            })
          ),
        }));
      } catch (error) {
        console.error(error);
      }
    };

    if (params?.idAssistServico) getValues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAcceptSurplus = () => {
    api
      .post(Endpoints.VALORES_EXCEDENTES_AUTORIZAR + values?.idValorServico, {
        autorizado: true,
      })
      .then(() => {
        setVisible({
          confirm: true,
          reject: false,
        });
      })
      .catch((error) => {
        console.log(error);
        history.replace(PATHS.notfound);
      });
  };

  const handleRejectSurplus = () => {
    api
      .post(Endpoints.VALORES_EXCEDENTES_AUTORIZAR + values?.idValorServico, {
        autorizado: false,
      })
      .then(() => {
        setVisible(undefined);
        history.replace(PATHS.valor_excedente_finalizado, data);
      })
      .catch((error) => {
        console.log(error);
        history.replace(PATHS.notfound);
      });
  };

  const renderTextModal = () => {
    if (!modalVisible) return null;

    if (modalVisible?.confirm) {
      return (
        <TextModal>
          Muito obrigado por confirmar, a clínica será notificada de sua aprovação.
        </TextModal>
      );
    }

    if (modalVisible?.reject) {
      return (
        <TextModal>
          Ok! Daremos continuidade no procedimento dentro da cobertura do seu plano.
          <br />
          <br />
          <TextModal isCancel>
            A não autorização isenta a Assistência 24 horas por qualquer complicação futura devido a
            procedimentos realizados em locais não credenciados.
          </TextModal>
          <br />
          <br />
          Qualquer dúvida entre em contato com a nossa central {data.clienteCorporativoTelefone}
        </TextModal>
      );
    }

    return (
      <TextModal>
        {data.nomeCliente}, o pagamento do valor excedente no total de{' '}
        {Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: 'BRL',
        }).format(values.excedente)}{' '}
        será realizada diretamente com a clínica.
      </TextModal>
    );
  };

  return (
    <main>
      <Steps steps={steps} />
      {!values ? (
        <LoadingComponent height={285} width={285} />
      ) : (
        <>
          <Title>Resumo da cobertura</Title>

          <Board isSmall>
            <li>
              <span>Cobertura total: </span>
              {Intl.NumberFormat('pt-BR', {
                style: 'currency',
                currency: 'BRL',
              }).format(values.cobertura)}
            </li>
          </Board>

          <Board>
            <ul>
              <li>
                <span>Procedimentos</span>
              </li>
              {values.procedimentos.map(({ id, descricao, valor }) => (
                <Fragment key={String(id)}>
                  <br />
                  <li>
                    <span>{descricao}</span>
                  </li>
                  <li>
                    <span>Valor: </span>
                    {Intl.NumberFormat('pt-BR', {
                      style: 'currency',
                      currency: 'BRL',
                    }).format(valor)}
                  </li>
                </Fragment>
              ))}
              <li value='last'>
                <span>Valor: </span>
                {Intl.NumberFormat('pt-BR', {
                  style: 'currency',
                  currency: 'BRL',
                }).format(values.total)}
              </li>
            </ul>
          </Board>

          <Line>
            <Board isSmall>
              <li>
                <span>Procedimentos</span>
              </li>
              <li>
                {Intl.NumberFormat('pt-BR', {
                  style: 'currency',
                  currency: 'BRL',
                }).format(values.total)}
              </li>
            </Board>

            <Board isSmall>
              <li>
                <span>Cobertura total</span>
              </li>
              <li>
                {Intl.NumberFormat('pt-BR', {
                  style: 'currency',
                  currency: 'BRL',
                }).format(values.cobertura)}
              </li>
            </Board>

            <Board isImportant isSmall>
              <li>
                <span>Excedente</span>
              </li>
              <li>
                {Intl.NumberFormat('pt-BR', {
                  style: 'currency',
                  currency: 'BRL',
                }).format(values.excedente)}
              </li>
            </Board>
          </Line>

          <Message>
            <Title>
              Os valores detalhados acima ultrapassam o limite da sua cobertura.
              <br />
              Você autoriza a cobrança dos valores adicionais?
            </Title>
          </Message>

          <Button
            handlePress={() => {
              setVisible({
                confirm: false,
                reject: false,
              });
            }}
          >
            <p>Sim, estou ciente do valor excedente</p>
          </Button>

          <Button
            handlePress={() => {
              setVisible({
                confirm: false,
                reject: true,
              });
            }}
          >
            <p>Não autorizo a cobrança de valores adicionais</p>
          </Button>

          <Modal
            isVisible={!!modalVisible}
            onClose={() => {
              if (modalVisible?.confirm) {
                setVisible(undefined);
                history.replace(PATHS.valor_excedente_finalizado, data);
              } else {
                setVisible({
                  confirm: false,
                  reject: false,
                });
              }
            }}
            buttonText={modalVisible?.confirm ? undefined : 'Confirmar'}
            renderText={renderTextModal}
            onPress={() => {
              if (modalVisible?.reject) {
                handleRejectSurplus();
              } else {
                handleAcceptSurplus();
              }
            }}
          />
        </>
      )}
    </main>
  );
}

export default ValorExcedente;
